import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { parseISO, differenceInCalendarDays, format } from "date-fns";
import { phaseColor } from "@/lib/constants";
import { money } from "@/components/BudgetView";
import { toast } from "sonner";
import { Flag, Loader2, TrendingUp, TrendingDown, Minus, GitCompare } from "lucide-react";

function VarBadge({ days }) {
  const color = days > 0 ? "#EF4444" : days < 0 ? "#10B981" : "#64748b";
  const Icon = days > 0 ? TrendingUp : days < 0 ? TrendingDown : Minus;
  const label = days > 0 ? `+${days}d` : days < 0 ? `${days}d` : "0d";
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded" style={{ color, background: `${color}1a` }}>
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export default function BaselineView({ projectId, project, tasks, phaseColors, onReload }) {
  const [budget, setBudget] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadBudget = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/budget`); setBudget(data); } catch (e) {}
  };
  useEffect(() => { loadBudget(); }, [projectId, tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasBaseline = !!project?.baseline_set_at;

  const setBaseline = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/baseline`);
      toast.success("Baseline captured");
      await onReload?.();
      loadBudget();
    } catch (e) { toast.error("Could not set baseline"); }
    finally { setSaving(false); }
  };

  const rows = useMemo(() => {
    return tasks
      .filter((t) => t.baseline_start && t.baseline_end && t.start_date && t.end_date)
      .map((t) => {
        const startVar = differenceInCalendarDays(parseISO(t.start_date), parseISO(t.baseline_start));
        const finishVar = differenceInCalendarDays(parseISO(t.end_date), parseISO(t.baseline_end));
        return { ...t, startVar, finishVar };
      });
  }, [tasks]);

  const summary = useMemo(() => {
    const slipped = rows.filter((r) => r.finishVar > 0).length;
    const ahead = rows.filter((r) => r.finishVar < 0).length;
    const maxSlip = rows.reduce((m, r) => Math.max(m, r.finishVar), 0);
    return { slipped, ahead, maxSlip, tracked: rows.length };
  }, [rows]);

  if (!hasBaseline) {
    return (
      <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="baseline-empty">
        <Flag className="h-8 w-8 text-primary mx-auto mb-3" />
        <h3 className="font-display text-2xl font-bold tracking-tight">No baseline yet</h3>
        <p className="text-muted-foreground mt-2 mb-6 max-w-md mx-auto">Capture a baseline to freeze the current schedule and costs. As the plan changes, you'll see the variance against this snapshot.</p>
        <Button className="gap-2" onClick={setBaseline} disabled={saving} data-testid="set-baseline-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag className="h-4 w-4" />} Set baseline
        </Button>
      </div>
    );
  }

  const cur = budget?.currency || "USD";

  return (
    <div className="space-y-6" data-testid="baseline-view">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <GitCompare className="h-4 w-4" /> Baseline captured {format(parseISO(project.baseline_set_at), "MMM d, yyyy")} · comparing against current plan
        </p>
        <Button variant="outline" className="gap-2" onClick={setBaseline} disabled={saving} data-testid="rebaseline-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag className="h-4 w-4" />} Re-baseline
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Tasks slipped" value={summary.slipped} tint={summary.slipped ? "#EF4444" : "#10B981"} />
        <Kpi label="Ahead of plan" value={summary.ahead} tint="#10B981" />
        <Kpi label="Worst slip" value={`${summary.maxSlip}d`} tint={summary.maxSlip ? "#F59E0B" : "#64748b"} />
        <Kpi label="Cost variance" value={budget?.cost_variance == null ? "—" : money(budget.cost_variance, cur)} tint={(budget?.cost_variance || 0) > 0 ? "#EF4444" : "#10B981"} />
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-left">
              <th className="px-4 py-2.5 font-semibold">Task</th>
              <th className="px-4 py-2.5 font-semibold">Baseline start → finish</th>
              <th className="px-4 py-2.5 font-semibold">Current start → finish</th>
              <th className="px-4 py-2.5 font-semibold text-center">Start var</th>
              <th className="px-4 py-2.5 font-semibold text-center">Finish var</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b border-border/60 hover:bg-secondary/20" data-testid={`baseline-row-${r.id}`}>
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: (phaseColors && phaseColors[r.phase]) || phaseColor(r.phase) }} />
                    <span className="font-medium">{r.title}</span>
                  </span>
                </td>
                <td className="px-4 py-2.5 text-muted-foreground">{r.baseline_start} → {r.baseline_end}</td>
                <td className="px-4 py-2.5">{r.start_date} → {r.end_date}</td>
                <td className="px-4 py-2.5 text-center"><VarBadge days={r.startVar} /></td>
                <td className="px-4 py-2.5 text-center"><VarBadge days={r.finishVar} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, tint }) {
  return (
    <div className="border border-border rounded-lg p-5">
      <p className="overline text-muted-foreground">{label}</p>
      <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={{ color: tint }}>{value}</p>
    </div>
  );
}
